import { PAGE_SEO, VENDOR_PAGE_SEO } from './pageSeo';
import { CBR_PAGE, CBR_TIERS } from './cbrPricing';
import { blogList } from './blogPosts';
import {
  SALES_CHECKLIST_PAGE,
  OPERATIONS_CHECKLIST_PAGE,
} from '../config/checklistPageConfig';

/** Заголовки секций на статических страницах (H1/H2) */
export const SITE_PAGE_HEADINGS = {
  '/': [
    'Лицензии и подписки на ПО для компаний — покупка, учёт, продление',
    'Оптимизация рутинных задач',
    'Как это работает',
    'О компании',
  ],
  '/about': [
    'Интегратор IT-решений',
    'Обращение основателя',
    'Направления деятельности',
    'Как мы работаем',
    'Почему выбирают Антологию',
    'Нужен подбор IT-решения?',
  ],
  '/services': [
    'Внедрение ИИ-решений',
    'ИИ-решения',
    'ИИ-агенты для бизнеса',
    'Корпоративный RAG',
    'On-prem ИИ-разработка',
  ],
  '/ai-agents-platform': [
    'Память компании и команда агентов',
    'AI-based база данных',
    'Аутрич-агент',
    'Стратегический агент',
    'Проектный агент',
    'Хелпдеск-агент',
    'Кейсы на видео',
  ],
  '/enterprise-systems': [
    'Внедрение корпоративных систем',
    'Направления внедрения',
    'CRM и ERP',
    '1С',
    'Битрикс24',
    'Общее ПО',
    'Информационная безопасность',
    'Инфраструктурное ПО',
    'САПР',
  ],
  '/solutions': ['Каталог решений', 'Найдите подходящее решение для вашего бизнеса'],
  '/faq': ['Частые вопросы', 'Закупка лицензий на ПО и AI'],
  '/blog': ['Блог'],
  '/novosti-antologia': ['Новости Антологии', 'Все материалы'],
  '/novosti-antologia/novosti-antologii': ['Новости антологии', 'Партнёрства'],
  '/novosti-antologia/akcii-i-skidki': ['Акции и скидки', 'Спецпредложения'],
  '/novosti-antologia/novosti-vendorov': ['Новости вендоров', 'Релизы вендоров'],
  '/novosti-antologia/blog': ['Блог', 'Статьи об AI'],
  '/novosti-antologia/vebinary': ['Вебинары', 'Онлайн-мероприятия'],
  '/novosti-antologia/zapisi-vebinarov': ['Записи вебинаров', 'Архив вебинаров'],
  '/cbr': [
    CBR_PAGE.hero.title,
    CBR_PAGE.tariffs.title,
    CBR_PAGE.comparison.title,
    CBR_PAGE.startSection.title,
    CBR_PAGE.operationsSection.title,
    CBR_PAGE.proSection.title,
    CBR_PAGE.faq.title,
    ...CBR_TIERS.map((t) => t.name),
  ],
  '/ready-for-ai': [
    OPERATIONS_CHECKLIST_PAGE.hero.title,
    'Готовность компании к пилоту ИИ',
  ],
  '/ready-for-ai-sales': [
    SALES_CHECKLIST_PAGE.hero.title,
    'Готовы ли вы к Generative AI в продажах?',
  ],
  '/gen-ai-creative': [
    'Gen AI инфраструктура для команд',
    'С чем сталкиваются компании при внедрении ИИ',
    'Чем можем помочь',
    'Как проходит пилот',
    'Реальные кейсы',
  ],
};

function pageNameFromSeoTitle(title) {
  return title.split('|')[0].trim();
}

function createPageEntry(path, { name, description, headings = [], tags = [] }) {
  return {
    id: `page:${path}`,
    kind: 'page',
    path,
    name,
    short_description: description,
    headings,
    tags,
  };
}

/** Статический индекс страниц сайта для поиска в шапке */
export function buildSiteSearchIndex() {
  const byPath = new Map();

  Object.entries(PAGE_SEO).forEach(([path, seo]) => {
    byPath.set(
      path,
      createPageEntry(path, {
        name: pageNameFromSeoTitle(seo.title),
        description: seo.description,
        headings: SITE_PAGE_HEADINGS[path] || [],
      })
    );
  });

  Object.entries(VENDOR_PAGE_SEO).forEach(([slug, seo]) => {
    const path = `/solutions/${slug}`;
    byPath.set(
      path,
      createPageEntry(path, {
        name: pageNameFromSeoTitle(seo.title),
        description: seo.description,
        tags: [slug],
      })
    );
  });

  blogList.forEach((post) => {
    const path = `/blog/${post.slug}`;
    byPath.set(
      path,
      createPageEntry(path, {
        name: post.title,
        description: post.description,
        headings: [post.category].filter(Boolean),
        tags: [post.category],
      })
    );
  });

  byPath.set(
    '/blog',
    createPageEntry('/blog', {
      name: 'Блог',
      description: 'Статьи про внедрение ИИ и IT-решения для бизнеса',
      headings: SITE_PAGE_HEADINGS['/blog'],
    })
  );

  // Пункты меню без отдельного PAGE_SEO или с уточнёнными названиями
  const navExtras = [
    {
      path: '/cbr',
      name: 'Поддержка AI',
      description: 'ЦБР — центр быстрого реагирования, тарифы поддержки AI-сервисов',
      tags: ['цбр', 'поддержка', 'техподдержка', 'sla'],
    },
    {
      path: '/gen-ai-creative',
      name: 'Gen AI',
      description: 'Gen AI инфраструктура для команд: оплата ИИ-сервисов в рублях',
      tags: ['gen ai', 'креатив', 'инфраструктура', 'лицензии', 'оффер'],
    },
  ];

  navExtras.forEach(({ path, name, description, tags }) => {
    if (!byPath.has(path)) {
      byPath.set(
        path,
        createPageEntry(path, {
          name,
          description,
          headings: SITE_PAGE_HEADINGS[path] || [],
          tags,
        })
      );
    } else {
      const entry = byPath.get(path);
      entry.tags = [...new Set([...(entry.tags || []), ...(tags || [])])];
    }
  });

  return Array.from(byPath.values());
}

export const SITE_SEARCH_INDEX = buildSiteSearchIndex();

function normalize(text) {
  return (text || '').toLowerCase();
}

export function searchSitePages(query, index = SITE_SEARCH_INDEX) {
  const q = normalize(query.trim());
  if (!q) return [];

  return index
    .map((entry) => {
      const fields = [
        { key: 'name', text: entry.name },
        { key: 'description', text: entry.short_description },
        ...(entry.headings || []).map((h) => ({ key: 'heading', text: h })),
        ...(entry.tags || []).map((t) => ({ key: 'tag', text: t })),
      ];

      for (const field of fields) {
        if (normalize(field.text).includes(q)) {
          return {
            ...entry,
            matchContext:
              field.key === 'heading'
                ? `Раздел: ${field.text}`
                : field.key === 'tag'
                  ? field.text
                  : entry.short_description,
          };
        }
      }
      return null;
    })
    .filter(Boolean);
}

export function getSearchResultLink(item) {
  if (item.kind === 'page') return item.path;
  if (item.slug) return `/landing/${item.slug}`;
  if (item.link) return item.link;
  return `/product/${item.id}`;
}
